import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Autocomplete, AutocompleteItem, Button, Chip, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Tab, Tabs, useDisclosure } from '@heroui/react';
import { FaCheck, FaPlus, FaMicrophone, FaWandMagicSparkles, FaPlay, FaPause, FaEarthAmericas, FaSatelliteDish, FaLock, FaHeadphones, FaUpload, FaGlobe, FaTv } from 'react-icons/fa6';
import { useGlobalAudioPlayer } from 'react-use-audio-player';
import toast from 'react-hot-toast';
import ChannelAvatar from '../primitives/ChannelAvatar.jsx';
import SelectTile from '../primitives/SelectTile.jsx';
import VoiceCard from '../../../widgets/VoiceCard.jsx';
import repositories from '../../../repositories.jsx';
import { gradFor } from '../../../services/autoDub/mappers.js';
import { LANGUAGES, setLanguages, lang as findLang, resolveVoiceLanguages } from '../../../services/autoDub/mockData.js';

/* Per-language voice picker — one card per selected language. The "Change"
   modal mirrors the /voices page: an All Voices / My Voices tab switch over the
   same GET /projects/voice-list source (My Voices = the creator's own cloned
   voices), with search. The All Voices tab is pre-filtered to this language so
   you only pick a voice that can actually speak it. The committed value is the
   chosen SpeakerMaster id; its display details (name, sample, type) are cached
   in the draft's voiceMeta map so the card can render without a re-fetch. */
function VoicePicker({ code, value, meta, onPick, onResolveMeta }) {
	const l = findLang(code);
	const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
	const { load, stop, playing, src } = useGlobalAudioPlayer();
	const [pending, setPending] = useState(value);
	const [tab, setTab] = useState('all'); // 'all' | 'starred' | 'own'
	const [q, setQ] = useState('');

	// Same search suggestions the /voices page shows in its search box.
	const { data: voiceSuggestions } = useQuery({
		queryKey: ['voiceSuggestions'],
		queryFn: repositories.project.voiceSuggestions,
	});

	// Distinct voice-language labels in the catalog (e.g. "English (Australia)",
	// "Chinese (Cantonese)"). Used to expand this language code into every variant
	// its voices may be tagged with — see resolveVoiceLanguages.
	const { data: catalogLanguages } = useQuery({
		queryKey: ['voiceLanguages'],
		queryFn: repositories.allLanguages,
	});
	const languageParam = useMemo(
		() => resolveVoiceLanguages(code, (catalogLanguages || []).map(x => x?.language)).join(','),
		[code, catalogLanguages]
	);

	// Voices for the active tab. All Voices and Starred are pinned to this language
	// (matching every locale variant of it) since only same-language voices are
	// usable for this dub; My Voices (own clones) span every language, so it isn't
	// language-filtered.
	const list = useInfiniteQuery({
		queryKey: ['autoDubVoicePicker', tab, languageParam, q],
		queryFn: ({ pageParam }) => repositories.project.voiceList({
			page: pageParam, per_page: 28, key: q, search: q, provider: '',
			own_key: tab === 'own' ? 'cloned' : tab === 'starred' ? 'starred' : 'false',
			language: tab === 'own' ? '' : languageParam,
		}),
		initialPageParam: 1,
		getNextPageParam: (last, _pages, lastParam) => (last?.rows?.length > 27 ? lastParam + 1 : null),
		enabled: isOpen,
	});
	const voices = useMemo(() => (list.data?.pages || []).flatMap(p => p?.rows || []), [list.data]);

	// Sync the modal's pending pick with the committed value when it (re)opens.
	useEffect(() => { setPending(value); }, [value, isOpen]);

	// Edit/restore: we get an id back from the API but no display details. Backfill
	// the card's voiceMeta once the selected voice shows up in a loaded list.
	useEffect(() => {
		if (!value || meta) return;
		const found = voices.find(v => String(v.id) === String(value));
		if (found) onResolveMeta?.(value, found);
	}, [voices, value, meta, onResolveMeta]);

	const playSample = (url) => {
		if (!url || !url.startsWith('http')) { toast.error('No sample available for this voice.'); return; }
		if (src === url && playing) { stop(); return; }
		stop();
		load(url, { autoplay: true, format: 'mp3' });
	};
	const isPlayingSelected = !!meta && src === meta.speaker_voice && playing;
	const grad = gradFor(meta?.id ?? value ?? code);

	return (
		<div className="group relative bg-white rounded-2xl border border-slate-200 shadow-small overflow-hidden transition hover:shadow-medium hover:border-primary/40 hover:-translate-y-0.5">
			{/* scoped keyframes for the now-playing equalizer */}
			<style>{`@keyframes adVoiceViz{0%,100%{transform:scaleY(.28)}50%{transform:scaleY(1)}}`}</style>

			{/* gradient accent strip */}
			<div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${grad[0]}, ${grad[1]})` }} />

			{/* now-playing visualizer — anchored to the bottom-right corner, fading into the card background */}
			{isPlayingSelected && (
				<div className="pointer-events-none absolute bottom-0 right-0 flex items-end gap-[5px] h-16 opacity-50" aria-hidden="true">
					{[0, 1, 2, 3, 4, 5, 6].map(i => (
						<span
							key={i}
							className="w-1.5 h-full rounded-t-full"
							style={{ background: `linear-gradient(to top, ${grad[1]}, transparent)`, transformOrigin: 'bottom', animation: 'adVoiceViz 0.85s ease-in-out infinite', animationDelay: `${i * 0.12}s` }}
						/>
					))}
				</div>
			)}

			<div className="p-4">
				{/* header: language + change action */}
				<div className="flex items-center justify-between mb-3.5">
					<span className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-700">
						{l.name}
					</span>
					<Button size="sm" variant="light" className="text-primary font-semibold -mr-1"
						onPress={onOpen} startContent={<FaWandMagicSparkles size={12} />}>
						{value ? 'Change' : 'Choose'}
					</Button>
				</div>

				{/* body: voice avatar + name */}
				{meta ? (
					<div className="flex items-center gap-3.5">
						<button
							type="button"
							disabled={!meta.speaker_voice}
							onClick={() => playSample(meta.speaker_voice)}
							aria-label={`Play ${meta.speaker_name} sample`}
							className="relative w-12 h-12 rounded-full flex-none grid place-items-center text-white shadow-sm transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
							style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
						>
							{isPlayingSelected && (
								<span className="absolute inset-0 rounded-full animate-ping" style={{ background: grad[1], opacity: 0.35 }} />
							)}
							<span className="relative">{isPlayingSelected ? <FaPause size={15} /> : <FaPlay size={15} className="ml-0.5" />}</span>
						</button>
						<div className="min-w-0 flex-1">
							<div className="text-[14.5px] font-semibold text-slate-800 truncate">{meta.speaker_name}</div>
							<div className="mt-1 flex items-center gap-2">
								<Chip size="sm" variant="flat" className="capitalize bg-slate-100 text-slate-600 h-5">{meta.speaker_type || 'Voice'}</Chip>
							</div>
						</div>
					</div>
				) : value ? (
					/* id known but details not resolved yet (edit/restore) */
					<button type="button" onClick={onOpen}
						className="w-full flex items-center gap-3.5 rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-left hover:border-primary/40 transition">
						<span className="w-12 h-12 rounded-full flex-none grid place-items-center bg-slate-100 text-slate-400"><FaMicrophone size={16} /></span>
						<span className="text-[13px] text-slate-500">Voice selected · open to view</span>
					</button>
				) : (
					<button type="button" onClick={onOpen}
						className="w-full flex items-center gap-3.5 rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-left hover:border-primary/40 transition">
						<span className="w-12 h-12 rounded-full flex-none grid place-items-center bg-slate-100 text-slate-400"><FaMicrophone size={16} /></span>
						<span className="text-[13px] text-slate-400">Pick a voice for this language</span>
					</button>
				)}
			</div>

			{/* disableAnimation: the All/My-Voices Tab switch fires a react-query refetch
			    that can re-render the modal mid-close; with the framer-motion exit
			    animation that race intermittently leaves the overlay (role="dialog",
			    fixed inset-0 z-50) mounted over the page, making the whole app
			    unclickable. Closing synchronously (no exit animation) removes the race. */}
			<Modal isOpen={isOpen} onOpenChange={onOpenChange} size="3xl" scrollBehavior="inside" disableAnimation classNames={{ base: 'rounded-3xl' }}>
				<ModalContent>
					<ModalHeader className="flex flex-col gap-3">
						<span>Choose a {l.name} voice</span>
						<div className="flex items-center justify-between gap-3 flex-wrap">
							<Tabs selectedKey={tab} onSelectionChange={setTab} color="primary" size="md" aria-label="Voice source">
								<Tab key="all" title="All Voices" />
								<Tab key="own" title="My Voices" />
								<Tab key="starred" title="Starred" />
							</Tabs>
							<Autocomplete
								allowsCustomValue
								size="md"
								aria-label="Search voices"
								placeholder="Search here ..."
								className="max-w-xs min-h-11 font-normal"
								inputValue={q}
								onInputChange={setQ}
								startContent={<i className="hgi-stroke hgi-search-list-01 text-slate-500 text-lg" />}
							>
								{(voiceSuggestions?.suggestions || []).map(s => (
									<AutocompleteItem key={s}>{s}</AutocompleteItem>
								))}
							</Autocomplete>
						</div>
						{tab === 'all' && (
							<span className="text-[11.5px] font-normal text-slate-400">Showing voices that can speak {l.name}.</span>
						)}
						{tab === 'starred' && (
							<span className="text-[11.5px] font-normal text-slate-400">Showing your starred {l.name} voices.</span>
						)}
					</ModalHeader>
					<ModalBody>
						{list.isLoading ? (
							<div className="py-10 text-center text-slate-400 text-sm">Loading voices…</div>
						) : voices.length === 0 ? (
							tab === 'own' ? (
								<div className="py-10 text-center text-slate-400 text-sm">You haven't cloned any voices yet.</div>
							) : tab === 'starred' ? (
								<div className="py-10 text-center text-slate-400 text-sm">No starred {l.name} voices yet. Tap the star on any voice to save it here.</div>
							) : (
								<div className="py-10 text-center text-slate-400 text-sm">No {l.name} voices found.</div>
							)
						) : (
							<>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-2">
									{voices.map(sp => {
										const on = String(pending) === String(sp.id);
										return (
											<div
												key={sp.id}
												onClick={() => setPending(String(sp.id))}
												className={`rounded-3xl cursor-pointer transition ${on ? 'ring-2 ring-primary' : 'ring-1 ring-transparent hover:ring-slate-200'}`}
											>
												<VoiceCard data={sp} flat />
											</div>
										);
									})}
								</div>
								{list.hasNextPage && (
									<div className="flex justify-center py-3">
										<Button variant="flat" isLoading={list.isFetchingNextPage} onPress={() => list.fetchNextPage()}>Load more</Button>
									</div>
								)}
							</>
						)}
					</ModalBody>
					<ModalFooter>
						<Button variant="bordered" onPress={onClose}>Cancel</Button>
						<Button color="primary" isDisabled={!pending}
							onPress={() => {
								const picked = voices.find(v => String(v.id) === String(pending));
								onPick(String(pending), picked);
								onClose();
							}}>Use this voice</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</div>
	);
}

/* Step 3 — languages, destination mapping (new-video mode) and voice (PRD §8). */
export default function StepLanguages({ state, set, connect, channels = [], editing = false }) {
	const destChannels = channels.filter(c => c.id !== state.source?.id);

	// Available dub languages — same source as Setup your project → Translate
	// Language, minus 'auto' (you can't dub *into* auto-detect).
	const { data: languages = LANGUAGES } = useQuery({
		queryKey: ['get-project-languages', 'autodub'],
		queryFn: () => repositories.project.languages().then(list => {
			const mapped = (list || [])
				.filter(l => l.code !== 'auto')
				.map(l => ({ code: l.code, name: l.language }));
			setLanguages(mapped);
			return mapped;
		}),
	});

	const toggleLang = (code) => set(d => {
		const has = d.langs.includes(code);
		const langs = has ? d.langs.filter(c => c !== code) : [...d.langs, code];
		const mapping = { ...d.mapping };
		if (has) delete mapping[code]; else if (!mapping[code]) mapping[code] = [];
		// drop the voice pick for a language that's being removed
		const voiceByLang = { ...d.voiceByLang };
		if (has) delete voiceByLang[code];
		return { langs, mapping, voiceByLang };
	});

	const toggleDest = (code, destId) => set(d => {
		const cur = d.mapping[code] || [];
		const next = cur.includes(destId) ? cur.filter(x => x !== destId) : [...cur, destId];
		return { mapping: { ...d.mapping, [code]: next } };
	});

	return (
		<div>
			{/* scoped keyframes for the hero's floating-language motif */}
			<style>{`@keyframes adFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}`}</style>

			{/* gradient hero */}
			<div
				className="relative overflow-hidden rounded-3xl mb-7 px-6 py-7 md:px-9 md:py-9"
				style={{ background: 'linear-gradient(120deg,#2a0a4f 0%,#5A0AE0 52%,#8C2CE2 100%)' }}
			>
				<div className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
				<div className="pointer-events-none absolute -bottom-24 left-1/4 w-56 h-56 rounded-full bg-fuchsia-400/20 blur-3xl" />

				{/* floating language-script bubbles */}
				<div className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-8 hidden sm:flex items-center gap-2.5">
					{[{ c: 'あ', d: 0 }, { c: '文', d: 0.3 }, { c: 'A', d: 0.6 }, { c: 'ह', d: 0.9 }].map(s => (
						<span key={s.c} className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur grid place-items-center text-white text-[17px] font-bold" style={{ animation: 'adFloat 2.6s ease-in-out infinite', animationDelay: `${s.d}s` }}>{s.c}</span>
					))}
				</div>

				<div className="relative">
					<span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">
						<FaEarthAmericas size={13} /> Step 3 · Languages &amp; channels
					</span>
					<h2 className="mt-4 text-[26px] md:text-[30px] font-bold text-white tracking-tight leading-tight">Languages — and where the dubs go</h2>
					<p className="mt-2 text-[14.5px] text-white/80 leading-relaxed max-w-xl">
						Pick your languages, then decide if dubs live on the same video as an extra audio track, or get uploaded as new videos on your other channels.
					</p>
				</div>
			</div>

			{/* publish mode */}
			<div className="flex items-center gap-2.5 mb-3">
				<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center"><FaSatelliteDish size={14} /></span>
				<div className="text-[15px] font-semibold text-slate-700">How should dubs be published?</div>
			</div>
			{editing && (
				<div className="mb-3 text-[12px] text-slate-400 flex items-center gap-1.5"><FaLock size={11} /> How dubs are published can't be changed after creation.</div>
			)}
			<div className="flex gap-3.5 flex-wrap mb-6">
				<SelectTile
					selected={state.publishMode === 'new_video'} onClick={() => set({ publishMode: 'new_video' })} disabled={editing}
					emoji={<FaUpload size={20} color="#5A0AE0" />} iconColor="#5A0AE0" badge="Recommended"
					title="New video on my other channels"
					desc="Each dub is uploaded as its own video on a language channel you run — its own thumbnail, comments and analytics."
				/>
				<SelectTile
					selected={state.publishMode === 'audio_track'} onClick={() => set({ publishMode: 'audio_track' })} disabled={editing}
					emoji={<FaHeadphones size={20} color="#8C2CE2" />} iconColor="#8C2CE2"
					title="Same video, extra audio track"
					note="You'll need to download each dubbed track and upload it to YouTube manually."
				/>
			</div>

			{/* languages */}
			<div className="flex items-center gap-2.5 mb-3">
				<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center"><FaGlobe size={14} /></span>
				<div className="text-[15px] font-semibold text-slate-700">Which languages?</div>
				{state.langs.length > 0 && <span className="text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{state.langs.length} selected</span>}
			</div>
			<div className="flex gap-2.5 flex-wrap mb-6">
				{languages.map(l => {
					const on = state.langs.includes(l.code);
					return (
						<button key={l.code} type="button" onClick={() => toggleLang(l.code)}
							className={`flex items-center gap-2.5 px-4 py-2.5 rounded-[13px] border-[1.5px] transition ${on ? 'border-primary bg-primary/10 shadow-[0_0_0_3px_rgba(90,10,224,0.12)]' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
							<span className={`text-[14.5px] font-semibold ${on ? 'text-primary' : 'text-slate-600'}`}>{l.name}</span>
							{on && <FaCheck size={14} className="text-primary" />}
						</button>
					);
				})}
			</div>

			{/* audio-track note */}
			{state.langs.length > 0 && state.publishMode === 'audio_track' && (
				<div className="rounded-3xl p-[18px] flex gap-3.5 items-center mb-6 bg-secondary/10">
					<div className="w-[34px] h-[34px] rounded-xl bg-white text-secondary grid place-items-center flex-none"><FaCheck size={17} /></div>
					<div className="text-[12px] text-secondary leading-snug">
						No extra channels needed — we'll add {state.langs.length} audio track{state.langs.length > 1 ? 's' : ''} to each video on <b>{state.source?.name || 'your channel'}</b>. Viewers worldwide watch the same video in their own language.
					</div>
				</div>
			)}

			{/* voice — one picker per language; browse all AudioDub voices or your own clones */}
			{state.langs.length > 0 && (
				<>
					<div className="flex items-center gap-2.5 mb-1">
						<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center"><FaMicrophone size={14} /></span>
						<div className="text-[15px] font-semibold text-slate-700">Whose voice should viewers hear?</div>
					</div>
					<div className="text-[12px] text-slate-500 mb-3">Pick a voice for each language — browse all AudioDub voices, or choose one of your own cloned voices.</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
						{state.langs.map((code) => (
							<VoicePicker
								key={code}
								code={code}
								value={state.voiceByLang[code]}
								meta={state.voiceByLang[code] ? state.voiceMeta?.[state.voiceByLang[code]] : undefined}
								onPick={(id, v) => set(d => ({
									voiceByLang: { ...d.voiceByLang, [code]: id },
									voiceMeta: v ? { ...d.voiceMeta, [id]: v } : d.voiceMeta,
								}))}
								onResolveMeta={(id, v) => set(d => ({ voiceMeta: { ...d.voiceMeta, [id]: v } }))}
							/>
						))}
					</div>
				</>
			)}

			{/* destination mapping (new-video mode) */}
			{state.langs.length > 0 && state.publishMode === 'new_video' && (
				<>
					<div className="flex items-center gap-2.5 mb-1">
						<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center"><FaTv size={14} /></span>
						<div className="text-[15px] font-semibold text-slate-700">Which channel gets each language?</div>
					</div>
					<div className="text-[12px] text-slate-500 mb-3">Pick one channel per language — or several, if a dub should go to more than one place.</div>
					<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-1.5 mb-6">
						{state.langs.map((code, i) => {
							const l = findLang(code);
							const sel = state.mapping[code] || [];
							return (
								<div key={code} className={`px-3.5 py-3.5 flex gap-3.5 items-start flex-wrap ${i ? 'border-t border-slate-100' : ''}`}>
									<div className="flex items-center gap-2.5 w-[150px] flex-none pt-1.5">
										<span className="text-sm font-semibold text-slate-700">{l.name}</span>
									</div>
									<div className="flex gap-2 flex-wrap flex-1 min-w-[260px]">
										{destChannels.map(d => {
											const on = sel.includes(d.id);
											// A reauth-required channel stays visible (warning badge on the
											// avatar) and can be unselected, but can't be newly added.
											const dead = d.connected === false;
											const locked = dead && !on;
											return (
												<button key={d.id} type="button" onClick={() => { if (!locked) toggleDest(code, d.id); }}
													disabled={locked}
													title={dead ? 'Reconnect this channel on the Channels page first' : undefined}
													className={`flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full border-[1.5px] transition ${on ? 'border-primary bg-primary/10' : 'border-slate-200 bg-white'} ${locked ? 'opacity-60 cursor-not-allowed' : on ? '' : 'hover:border-slate-300'}`}>
													<ChannelAvatar ch={d} size={26} radius={8} />
													<span className={`text-[12.5px] ${on ? 'text-primary font-semibold' : 'text-slate-600 font-medium'}`}>{d.name}</span>
													{on && <FaCheck size={12} className="text-primary" />}
												</button>
											);
										})}
										<button type="button" onClick={() => connect('dest')}
											className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary border-[1.5px] border-dashed border-slate-300 bg-white px-3 py-2 rounded-full hover:border-slate-400 transition">
											<FaPlus size={12} />Connect channel
										</button>
									</div>
								</div>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
}
